import { AnimatePresence, motion } from "framer-motion";
import React, { memo, useCallback, useId, useMemo, useRef, useState } from "react";
import useOutsideClick from "../../hooks/useOutsideClick.ts";
import Icon from "../components/Icon.tsx";
import IconButton from "../components/IconButton.tsx";
import Separator from "../components/Separator.tsx";
import Divider from "../layout/Divider.tsx";
import { SPACINGS } from "../spacings.ts";
import "./data-entry.css";

export interface SelectInputOption {
	title: string;
	iconUri?: string;
	obj: any;
}

interface SelectFieldProps {
	label?: string;
	disabled?: boolean;
	value: string;
	onChange: (obj: any) => void;
    placeholder?: string;
	options: SelectInputOption[];
	onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	error?: boolean;
	onClear?: () => void;
	maxDropdownHeight?: number;
	paddingInput?: number;
	fontSizeLabel?: string;
}

interface SelectDropdownProps {
	options: SelectInputOption[];
	onChange: (obj: any) => void;
	selectedOption: string | null;
	inputText: string;
	ref: React.RefObject<HTMLDivElement>;
	maxDropdownHeight?: number;
}

const SelectDropdown = React.forwardRef<
	HTMLDivElement,
	Omit<SelectDropdownProps, "ref">
>(({ options, onChange, selectedOption, inputText, maxDropdownHeight }, ref) => {

	const mostrarEmptyState = useMemo(() => {
		if (options.length === 0 && inputText.length > 0) {
			return true;
		}
		return false;
	}, [options.length, inputText]);

	const mostrarOptions = useMemo(() => {
		return options.length > 0;
	}, [options]);

	const dropdownHeight = useMemo(() => {
        if (mostrarOptions) return Math.min(options.length * 40, maxDropdownHeight || Infinity);
		if (mostrarEmptyState) return 40;
		return 0;
	}, [mostrarOptions, mostrarEmptyState, options.length, maxDropdownHeight]);
	
	return (
		<motion.div
			key={useId()}
			ref={ref}
			initial={{ opacity: 0, height: 30 }}
			exit={{ opacity: 0, height: 0 }}
			animate={{
				opacity: 1,
				height: dropdownHeight,
			}}
			transition={{ duration: 0.3 }}
			className="select-field-dropdown"
		>
			{mostrarOptions ? (
				options.map((option,index) => {
					return (
						<motion.div
							key={`${option.obj?.usuario}-${index}`}
							className="select-field-option"
							onClick={() => {
								onChange(option);
							}}
							initial={{ backgroundColor: "#fff" }}
							whileHover={{ backgroundColor: "#DEEBFF" }}
							animate={{
								backgroundColor:
									selectedOption === option.title ? "#2684FF" : "#fff",
								color: selectedOption === option.title ? "#fff" : "#000",
							}}
							transition={{ duration: 0.2 }}
						>
							{option.title ?? ""}
						</motion.div>
					);
				})
			) : mostrarEmptyState ? (
				<motion.div
					className="select-field-dropdown-empty"
					key="empty-state"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
				>
					<span>Nenhuma opção encontrada</span>
				</motion.div>
			) : (
				<motion.div
					className="select-field-dropdown-empty"
					key="digite-para-buscar"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
				>
					<span>Digite para buscar</span>
				</motion.div>
			)}
		</motion.div>
	);
});

function SelectInputField({
	label,
	disabled = false,
    placeholder,
	options,
	value,
	onChange,
	onInputChange,
	error,
	onClear,
	maxDropdownHeight,
	paddingInput,
	fontSizeLabel = "12px",
}: SelectFieldProps) {
	const [isOpen, toggleDropdown, closeDropdown, dropdownRef, , toggleRef] =
		useOutsideClick();
	const inputRef = useRef<HTMLInputElement>(null);

	const [inputText, setInputText] = useState<string>("");

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setInputText(event.target.value);
			onInputChange?.(event);
		},
		[onInputChange]
	);

	const handleOptionSelect = useCallback(
		(selectedOption: any) => {
			onChange(selectedOption);
			setInputText("");
			closeDropdown();
		},
		[onChange, closeDropdown]
	);

	const displayedValue = useMemo(
		() => (isOpen ? inputText : value),
		[isOpen, inputText, value]
	);

	return (
		<div className="select-field-container">
			{label && <div
			style={{fontSize: fontSizeLabel}}
			 className="select-field-label">{label}</div>}
			<motion.div
				ref={toggleRef}
				className={`select-field-wrapper ${error ? "error" : ""}`}
				onClick={() => {
					if (!disabled) {
						toggleDropdown();
					}
				}}
				initial={{
					borderColor: "#f2f2f2",
				}}
				animate={{
					borderColor: error
						? "rgba(255, 0, 0, 1)"
						: isOpen
						? "#2684ff"
						: "#f2f2f2",
				}}
				style={{
					padding: !!paddingInput ? paddingInput : 0,
				}}
				transition={{ duration: 0.2, ease: "easeIn" }}
			>
				<input
					type="text"
					onChange={handleInputChange}
					value={displayedValue}
					disabled={disabled}
					placeholder={placeholder}
					className="select-field-input"
					ref={inputRef}
				/>
				<div className="select-field-icon">
					{onClear && (
						<motion.div
							initial={{ rotate: 0 }}
							whileHover={{ rotate: 90 }}
							transition={{ duration: 0.2, ease: "easeInOut" }}
							className="select-field-chevron"
						>
							<IconButton
								icon="X"
								size="md"
								borderColor="none"
								onClick={() => {
									setInputText("");
									onClear?.();
									inputRef.current?.focus();
								}}
								backgroundColor="rgba(0, 0, 0, 0)"
								theme="dark"
								disabled={disabled}
							/>
						</motion.div>
					)}
					<Separator size="xs" direction="horizontal" />
					<Divider direction="vertical" theme="light" />
					<Separator size="xs" direction="horizontal" />
					<motion.div
						initial={{ rotate: 0 }}
						animate={{ rotate: isOpen ? 90 : 0 }}
						transition={{ duration: 0.2, ease: "easeInOut" }}
						className="select-field-chevron"
					>
						<Icon
							name="ChevronRight"
							strokeWidth={SPACINGS["2xs"]}
							size="md"
							theme="grey"
						/>
					</motion.div>
				</div>
			</motion.div>
			{error && <div className="error-input-text">Campo obrigatório.</div>}
			<AnimatePresence>
				{isOpen && (
					<SelectDropdown
						key={options.length}
						ref={dropdownRef}
						options={options || []}
						selectedOption={value}
						onChange={(selectedOption) => handleOptionSelect(selectedOption)}
						inputText={inputText}
						maxDropdownHeight={maxDropdownHeight}
					/>
				)}
			</AnimatePresence>
		</div>
	);
}

export default memo(SelectInputField);
